import Vue from 'vue'

const History = {
  /**
   * Key to set in sessionStorage
   * @type {String}
   */
  _storageKey: 'vue.router.back.button.history',

  /**
   * Fallback if sessionStorage is not available
   * @type {Array}
   */
  _history: [] as string[],

  /**
   * Current path
   * @type {Integer}
   */
  _current: -1,

  /**
   * Check if sessionStorage is available
   */
  useSession: window.sessionStorage ? 1 : 0,

  /**
   * Install global property $routerHistory
   */
  install () {
    Object.defineProperty(Vue.prototype, '$routerHistory', {
      get () {
        return History
      }
    })
  },

  /**
   * Reset the history, mainly for testing
   */
  reset () {
    this._history = []
    this._current = -1

    this.save()
  },

  /**
   * Get full history list
   * @method
   * @return {Array}
   */
  getHistory () {
    if (!this.useSession) {
      return this._history
    }

    const session = sessionStorage.getItem(this._storageKey)

    if (!session) {
      this._history = []
    } else {
      this._history = JSON.parse(session).history
    }

    return this._history
  },

  /**
   * Get current index
   * @method
   * @return {Array}
   */
  getCurrent () {
    if (!this.useSession) {
      return this._current
    }

    const session = sessionStorage.getItem(this._storageKey)

    if (!session) {
      this._current = -1
    } else {
      this._current = JSON.parse(session).current
    }

    return this._current
  },

  /**
   * Save to session
   */
  save () {
    if (this.useSession) {
      const session = JSON.stringify({
        history: this._history,
        current: this._current
      })

      sessionStorage.setItem(this._storageKey, session)

      return this
    }
  },

  /**
   * Get the previous path
   */
  previous () {
    const history = this.getHistory()

    if (history.length > 1) {
      return {path: history[this._current - 1]}
    }

    return {path: null}
  },

  /**
   * Get the current path
   */
  current () {
    const history = this.getHistory()

    if (history.length > 1) {
      return {path: history[this._current]}
    }

    return {path: null}
  },

  /**
   * Can we go back in history?
   */
  hasPrevious () {
    const current = this.getCurrent()

    return current > 0
  },


  /**
   * Add new route to the history
   */
  push (path: string) {
    this._history = this.getHistory()
    this._current = this.getCurrent()

    this._history.splice(this._current + 1, this._history.length)

    const currentPath = this._history[this._history.length - 1]

    if (currentPath !== path) {
      this._history.push(path)
      this._current = this._current + 1
    }

    this.save()
  },

  /**
   * User went back in history
   */
  back () {
    this._current = this.getCurrent()
    this._current = this._current - 1

    this.save()
  },

  /**
   * Get the index of recently visited route
   */
  indexOfRecentHistory (path: string) {
    const history = this.getHistory()
    const current = this.getCurrent()

    const recentHistory = history.slice(0, current + 1).reverse()

    return recentHistory.indexOf(path)
  },

  /**
   * Check if user visited a path recently
   * If so, the user must go back
   */
  visitedRecently (path: string) {
    const index = this.indexOfRecentHistory(path)

    return index !== -1
  }
}

export default History